import React, { useEffect, useMemo, useState } from "react";
import { FiFileText } from "react-icons/fi";
import { FaCube, FaBuilding } from "react-icons/fa";

export default function TearsheetsNews({
                                           contextPath,
                                           modules = ["EQ", "FI", "CM"],
                                           newDays = 30,
                                           updatedDays = 20,
                                           height = 575,
                                           openSheet,
                                           initialTab = "products", // 'products' | 'vendors'
                                       }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [activeTab, setActiveTab] = useState(
        initialTab === "vendors" ? "vendors" : "products"
    );

    const url = useMemo(() => {
        const qs = new URLSearchParams({
            modules: modules.join(","),
            newDays: String(newDays),
            updatedDays: String(updatedDays),
        }).toString();
        return `${contextPath}/tearsheets/whats-new?${qs}`;
    }, [contextPath, modules, newDays, updatedDays]);

    useEffect(() => {
        setLoading(true);
        setErr("");

        fetch(url, {
            headers: {
                Authorization: `Bearer ${localStorage.getItem("token")}`,
                "Content-Type": "application/json",
            },
        })
            .then(async (r) => {
                if (!r.ok) throw new Error((await r.text()) || r.statusText);
                return r.json();
            })
            .then((json) => setData(json))
            .catch((e) => {
                if (e.name !== "AbortError") setErr(e.message || "Failed to load");
            })
            .finally(() => setLoading(false));
    }, [url]);

    const handleOpenProduct = (id, name) => {
        if (openSheet) return openSheet("product", id, name || `Product #${id}`);
    };

    const handleOpenVendor = (id, name) => {
        if (openSheet) return openSheet("vendor", id, name || `Vendor #${id}`);
    };

    const Spinner = () => (
        <div className="flex items-center gap-2 text-sm text-gray-700" role="status" aria-live="polite">
            <div className="h-5 w-5 rounded-full border-2 border-gray-300 border-t-gray-600 animate-spin" />
            <span>Loading…</span>
        </div>
    );

    const ItemList = ({ items, isProduct }) => {
        if (!items || items.length === 0) {
            return <div className="text-sm text-gray-500">None</div>;
        }
        return (
            <ul className="space-y-2">
                {items.map((it) => (
                    <li key={`${isProduct ? "p" : "v"}-${it.id}`} className="flex items-center gap-2">
                        <button
                            onClick={() =>
                                isProduct
                                    ? handleOpenProduct(it.id, it.name)
                                    : handleOpenVendor(it.id, it.name)
                            }
                            className="inline-flex items-center gap-2 group"
                            title={`Open ${isProduct ? "Product" : "Vendor"} Tearsheet`}
                        >
                            <FiFileText className="w-4 h-4 text-[#25408f]" />
                            <span className="text-sm text-black group-hover:underline">
                {it.name || (isProduct ? `Product #${it.id}` : `Vendor #${it.id}`)}
              </span>
                        </button>
                        {it.auditDate && (
                            <span className="ml-2 text-xs text-gray-500">
                {new Date(it.auditDate).toLocaleDateString(undefined, {
                    day: "2-digit",
                    month: "2-digit",
                    year: "numeric",
                })}
              </span>
                        )}
                    </li>
                ))}
            </ul>
        );
    };

    const TabButton = ({ k, label, Icon }) => {
        const selected = activeTab === k;
        return (
            <button
                role="tab"
                aria-selected={selected}
                onClick={() => setActiveTab(k)}
                className="inline-flex items-center gap-2 px-3 py-2 rounded-t-lg border-b-2 transition"
                style={{
                    borderColor: selected ? "var(--rusty-orange)" : "transparent",
                    color: selected ? "var(--rusty-orange)" : "#4b5563",
                    fontWeight: selected ? 600 : 500,
                }}
            >
                <Icon className="w-4 h-4" />
                <span>{label}</span>
            </button>
        );
    };

    return (
        <div
            className="w-full overflow-auto rounded-md bg-white"
            style={{ height }}
        >
            <div className="text-2xl font-semibold text-[#25408f] mb-2">
                New &amp; Recently Updated Tearsheets 3di
            </div>
            <hr className="mb-4" />

            {/* Tabs */}
            <div role="tablist" aria-label="Tearsheet type" className="flex gap-2 mb-4">
                <TabButton k="products" label="Products" Icon={FaCube} />
                <TabButton k="vendors" label="Vendors" Icon={FaBuilding} />
            </div>

            {loading && <Spinner />}
            {err && <div className="text-sm text-red-600">{err}</div>}

            {data && !loading && (
                <>
                    {activeTab === "products" ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* New Products */}
                            <div>
                                <div className="text-lg font-semibold text-[#25408f] mb-1">New</div>
                                <hr className="mb-3" />
                                <ItemList items={data?.newly?.products} isProduct />
                            </div>

                            {/* Updated Products */}
                            <div>
                                <div className="text-lg font-semibold text-[#25408f] mb-1">Recently Updated</div>
                                <hr className="mb-3" />
                                <ItemList items={data?.updated?.products} isProduct />
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* New Vendors */}
                            <div>
                                <div className="text-lg font-semibold text-[#25408f] mb-1">New</div>
                                <hr className="mb-3" />
                                <ItemList items={data?.newly?.vendors} isProduct={false} />
                            </div>

                            {/* Updated Vendors */}
                            <div>
                                <div className="text-lg font-semibold text-[#25408f] mb-1">Recently Updated</div>
                                <hr className="mb-3" />
                                <ItemList items={data?.updated?.vendors} isProduct={false} />
                            </div>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}
